import { useEffect, useRef } from "react";

interface ShortcutDef {
  keys: string[];
  description: string;
}

interface ShortcutCategory {
  title: string;
  shortcuts: ShortcutDef[];
}

const SHORTCUT_CATEGORIES: ShortcutCategory[] = [
  {
    title: "Navigation",
    shortcuts: [
      { keys: ["Alt", "Tab"], description: "Cycle windows" },
      { keys: ["Alt", "Shift", "Tab"], description: "Cycle windows (reverse)" },
      { keys: ["Ctrl", "K"], description: "Spotlight search" },
      { keys: ["Ctrl", "Space"], description: "Spotlight search" },
      { keys: ["Ctrl", "1"], description: "Open pinned app 1" },
      { keys: ["Ctrl", "2"], description: "Open pinned app 2" },
      { keys: ["Ctrl", "3\u20139"], description: "Open pinned app 3\u20139" },
    ],
  },
  {
    title: "Windows",
    shortcuts: [
      { keys: ["Ctrl", "W"], description: "Close focused window" },
      { keys: ["Alt", "F4"], description: "Close focused window" },
      { keys: ["Alt", "M"], description: "Minimize window" },
      { keys: ["Alt", "Enter"], description: "Toggle maximize" },
      { keys: ["F11"], description: "Toggle maximize" },
      { keys: ["Ctrl", "D"], description: "Show desktop / restore" },
    ],
  },
  {
    title: "Snapping",
    shortcuts: [
      { keys: ["Ctrl", "\u2190"], description: "Snap left" },
      { keys: ["Ctrl", "\u2192"], description: "Snap right" },
      { keys: ["Ctrl", "\u2191"], description: "Maximize" },
      { keys: ["Ctrl", "\u2193"], description: "Restore from maximize" },
    ],
  },
  {
    title: "Apps",
    shortcuts: [
      { keys: ["Ctrl", "`"], description: "Open Chat" },
      { keys: ["Ctrl", ","], description: "Open Settings" },
      { keys: ["Ctrl", "L"], description: "Personalization menu" },
    ],
  },
  {
    title: "System",
    shortcuts: [
      { keys: ["Ctrl", "/"], description: "This overlay" },
    ],
  },
];

function Kbd({ children }: { children: string }) {
  return (
    <span
      style={{
        display: "inline-block",
        background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: 4,
        padding: "2px 7px",
        fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', monospace",
        fontSize: 11,
        lineHeight: "18px",
        color: "rgba(255,255,255,0.85)",
        minWidth: 22,
        textAlign: "center",
        boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
      }}
    >
      {children}
    </span>
  );
}

interface KeyboardShortcutsOverlayProps {
  onClose: () => void;
}

export function KeyboardShortcutsOverlay({ onClose }: KeyboardShortcutsOverlayProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [onClose]);

  // Close on click outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  return (
    <div
      className="keyboard-shortcuts-overlay"
      onClick={handleBackdropClick}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        animation: "ks-fade-in 0.15s ease-out",
      }}
    >
      <div
        ref={panelRef}
        style={{
          background: "rgba(22,27,34,0.92)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 12,
          padding: "28px 32px 24px",
          maxWidth: 720,
          width: "90vw",
          maxHeight: "80vh",
          overflowY: "auto",
          boxShadow: "0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)",
          animation: "ks-scale-in 0.15s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
            paddingBottom: 14,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(79,143,255,0.8)"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="20"
              height="20"
            >
              <rect x="2" y="6" width="20" height="12" rx="2" />
              <path d="M6 10h0 M10 10h0 M14 10h0 M18 10h0 M8 14h8" />
            </svg>
            <span
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "rgba(255,255,255,0.9)",
                letterSpacing: "-0.01em",
              }}
            >
              Keyboard Shortcuts
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(255,255,255,0.4)"
              strokeWidth="2"
              strokeLinecap="round"
              width="16"
              height="16"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Shortcut grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "20px 32px",
          }}
        >
          {SHORTCUT_CATEGORIES.map((cat) => (
            <div key={cat.title}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "rgba(79,143,255,0.7)",
                  marginBottom: 10,
                }}
              >
                {cat.title}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {cat.shortcuts.map((sc, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "5px 0",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12.5,
                        color: "rgba(255,255,255,0.65)",
                      }}
                    >
                      {sc.description}
                    </span>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        marginLeft: 12,
                        flexShrink: 0,
                      }}
                    >
                      {sc.keys.map((k, ki) => (
                        <span key={ki} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                          {ki > 0 && (
                            <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 10 }}>+</span>
                          )}
                          <Kbd>{k}</Kbd>
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div
          style={{
            marginTop: 20,
            paddingTop: 14,
            borderTop: "1px solid rgba(255,255,255,0.06)",
            textAlign: "center",
            fontSize: 11,
            color: "rgba(255,255,255,0.3)",
          }}
        >
          Press <Kbd>Esc</Kbd> or click outside to dismiss
        </div>
      </div>

      <style>{`
        @keyframes ks-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes ks-scale-in {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
